import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Database, RefreshCw, AlertCircle, CheckCircle2, 
  Clock, HardDrive, Download, Search, Settings,
  Edit, Trash2, Plus, X, Terminal, Play, Info
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const YiqiImportPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [importing, setImporting] = useState({}); // { endpointId: true/false }
  const [results, setResults] = useState({}); // { endpointId: { records, duration_seconds } }
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Import All State
  const [isImportAllModalOpen, setIsImportAllModalOpen] = useState(false);
  const [importAllLogs, setImportAllLogs] = useState([]);
  const [isImportingAll, setIsImportingAll] = useState(false);
  const [resetRagOnImportAll, setResetRagOnImportAll] = useState(true);

  // ABM State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    entity_name: '',
    smartie_name: '',
    smartie_id: '',
    schema_id: '',
    description: '',
    auto_index_rag: true
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchEndpoints = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [epRes, histRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/yiqi-import/endpoints`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/yiqi-import/history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (!epRes.ok || !histRes.ok) {
        throw new Error(`Error al cargar datos del servidor`);
      }
      
      const endpointsData = await epRes.json();
      const historyData = await histRes.json();
      
      setEndpoints(endpointsData);
      
      const initialResults = {};
      Object.entries(historyData).forEach(([id, log]) => {
        initialResults[id] = {
          records: log.records,
          duration: log.duration,
          timestamp: new Date(log.timestamp).toLocaleTimeString(),
          date: new Date(log.timestamp).toLocaleDateString()
        };
      });
      setResults(initialResults);
      
    } catch (err) {
      setError(err.message);
      toast.error('Error al cargar panel: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchEndpoints();
  }, [fetchEndpoints]);

  const handleImport = async (endpointId) => {
    setImporting(prev => ({ ...prev, [endpointId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/admin/yiqi-import/execute/${endpointId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error en la importación');
      }
      
      setResults(prev => ({ 
        ...prev, 
        [endpointId]: { 
          records: data.records, 
          duration: data.duration_seconds,
          timestamp: new Date().toLocaleTimeString(),
          date: new Date().toLocaleDateString()
        } 
      }));

      if (data.available_fields) {
        setEndpoints(prev => prev.map(ep => 
          ep.id === endpointId 
            ? { ...ep, available_fields: data.available_fields } 
            : ep
        ));
      }
      
      toast.success(`Importación de ${data.entity} completada: ${data.records} registros`);
    } catch (err) {
      toast.error(`Error importando ${endpointId}: ${err.message}`);
    } finally {
      setImporting(prev => ({ ...prev, [endpointId]: false }));
    }
  };

  const handleImportAll = async () => {
    setIsImportingAll(true);
    setImportAllLogs(['Iniciando sincronización masiva...']);
    const ragEnabled = endpoints.filter(e => e.auto_index_rag);

    // STEP 0: Reset FAISS index if requested
    if (resetRagOnImportAll && ragEnabled.length > 0) {
      setImportAllLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] 🗑️ Reseteando índice RAG (FAISS) para re-indexar desde cero...`]);
      try {
        await fetch(`${API_URL}/api/admin/embeddings/reset`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setImportAllLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ✔ Índice RAG reseteado. Comenzando importaciones...`]);
      } catch (err) {
        setImportAllLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ⚠️ No se pudo resetear el RAG: ${err.message}`]);
      }
    }
    
    // STEP 1: Import all endpoints
    for (const ep of endpoints) {
      setImportAllLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ⬇️ Importando: ${ep.entity_name} (${ep.id})...`]);
      try {
        const response = await fetch(`${API_URL}/api/admin/yiqi-import/execute/${ep.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Error en la importación');
        }
        
        const ragNote = ep.auto_index_rag ? ' · 🧠 RAG indexando en background...' : '';
        setImportAllLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ✔ ${ep.entity_name} — ${data.records} regs en ${data.duration_seconds}s${ragNote}`]);
        
        setResults(prev => ({ 
          ...prev, 
          [ep.id]: { 
            records: data.records, 
            duration: data.duration_seconds,
            timestamp: new Date().toLocaleTimeString(),
            date: new Date().toLocaleDateString()
          } 
        }));

        if (data.available_fields) {
          setEndpoints(prev => prev.map(e => 
            e.id === ep.id 
              ? { ...e, available_fields: data.available_fields } 
              : e
          ));
        }
        
      } catch (err) {
        setImportAllLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ❌ Error en ${ep.entity_name}: ${err.message}`]);
      }
    }
    
    const ragCount = ragEnabled.length;
    const ragSummary = ragCount > 0
      ? `\n[${new Date().toLocaleTimeString()}] 🧠 RAG indexando ${ragCount} colección(es) en background (${ragEnabled.map(e=>e.id).join(', ')}). Revisá admin/consumo para ver el progreso.`
      : '';
    setImportAllLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ✅ Sincronización masiva finalizada.${ragSummary}`]);
    setIsImportingAll(false);
  };

  // ABM Functions
  const handleOpenModal = (endpoint = null) => {
    if (endpoint) {
      setEditingId(endpoint.id);
      setFormData({
        id: endpoint.id,
        entity_name: endpoint.entity_name,
        smartie_name: endpoint.smartie_name || '',
        smartie_id: endpoint.smartie_id,
        schema_id: endpoint.schema_id || '',
        description: endpoint.description || '',
        auto_index_rag: endpoint.auto_index_rag || false
      });
    } else {
      setEditingId(null);
      setFormData({
        id: '',
        entity_name: '',
        smartie_name: '',
        smartie_id: '',
        schema_id: '',
        description: '',
        auto_index_rag: true
      });
    }
    setIsModalOpen(true);
  };

  const handleSaveEndpoint = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/admin/yiqi-import/endpoints`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al guardar endpoint');
      }

      toast.success(editingId ? 'Endpoint actualizado' : 'Endpoint creado');
      setIsModalOpen(false);
      fetchEndpoints();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteEndpoint = async (id) => {
    if (!window.confirm(`¿Estás seguro de eliminar el endpoint "${id}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/yiqi-import/endpoints/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al eliminar endpoint');
      }

      toast.success('Endpoint eliminado');
      fetchEndpoints();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredEndpoints = endpoints.filter(ep => 
    ep.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ep.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderTableBody = () => {
    if (filteredEndpoints.length === 0) {
      return (
        <div className="px-6 py-12 text-center text-gray-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-20" />
          No se encontraron endpoints que coincidan con la búsqueda.
        </div>
      );
    }

    return filteredEndpoints.map((ep) => (
      <div key={ep.id} className="grid grid-cols-[1.8fr,0.8fr,0.8fr,1.4fr,1.5fr,1.2fr] hover:bg-gray-700/20 transition-colors items-center border-t border-gray-700/50">
        <div className="px-6 py-4">
          <div className="font-medium text-blue-100">{ep.id.toUpperCase()}</div>
          <div className="text-[10px] text-gray-500 font-mono mb-2 uppercase tracking-tighter flex items-center gap-2">
            entity: {ep.entity_name}
            {ep.description && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    className="text-gray-600 hover:text-emerald-400 transition-colors cursor-help"
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[250px] bg-gray-900 border-gray-700 text-gray-200">
                  {ep.description}
                </TooltipContent>
              </Tooltip>
            )}
            {ep.auto_index_rag && (
              <span className="bg-emerald-500/10 text-emerald-400 px-1 rounded border border-emerald-500/20 text-[8px] animate-pulse">
                RAG AUTO-SYNC
              </span>
            )}
          </div>
          
          {ep.available_fields && ep.available_fields.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {ep.available_fields.map((f, idx) => (
                <span key={idx} className="bg-gray-700/50 text-gray-400 text-[9px] px-1.5 py-0.5 rounded border border-gray-600/30">
                  {f}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 py-4">
          <span className="bg-gray-900 px-2 py-1 rounded text-xs font-mono text-cyan-400">
            {ep.schema_id || 'Global'}
          </span>
        </div>
        <div className="px-6 py-4">
          <span className="bg-blue-900/30 px-2 py-1 rounded text-xs font-mono text-blue-300 border border-blue-500/20">
            {ep.smartie_id}
          </span>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 text-sm">
            <HardDrive className="w-4 h-4 text-purple-400" />
            <span className="text-gray-300 font-mono text-xs">
              {(ep.smartie_name || ep.entity_name)?.replace(/ /g, '_').toUpperCase()}
            </span>
          </div>
        </div>
        <div className="px-6 py-4">
          {results[ep.id] ? (
            <div className="space-y-1">
              <div className="text-sm text-green-400 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {results[ep.id].records.toLocaleString()} reg.
              </div>
              <div className="text-[10px] text-gray-500 flex flex-col">
                 <span><Clock className="w-2.5 h-2.5 inline mr-1" /> {results[ep.id].duration}s</span>
                 <span className="opacity-70">{results[ep.id].date} @ {results[ep.id].timestamp}</span>
              </div>
            </div>
          ) : (
            <span className="text-gray-600 italic text-sm">- sin datos -</span>
          )}
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2">
          {/* ABM Actions */}
          <div className="flex bg-gray-900/50 rounded-lg p-1 border border-gray-700/50 mr-2">
            <button 
              onClick={() => handleOpenModal(ep)}
              className="p-1.5 text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10 rounded transition-colors"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handleDeleteEndpoint(ep.id)}
              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Import Action */}
          <button
            onClick={() => handleImport(ep.id)}
            disabled={importing[ep.id]}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              importing[ep.id] 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : 'bg-cyan-600 hover:bg-cyan-500 text-white shadow-lg shadow-cyan-900/20 active:scale-95'
            }`}
          >
            {importing[ep.id] ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <React.Fragment>
                <Download className="w-4 h-4" />
                <span className="hidden lg:inline">Importar</span>
              </React.Fragment>
            )}
          </button>
        </div>
      </div>
    ));
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-200 mb-2">Error de Conexión</h2>
          <p className="text-red-300/80 mb-6">{error}</p>
          <button onClick={() => window.location.reload()} className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg transition-colors">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="text-gray-100">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent flex items-center gap-3">
              <Database className="w-7 h-7 text-[#00a1df]" />
              Importación YIQI
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Sincronización de datos estructurados para entrenamiento y análisis RAG
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setIsImportAllModalOpen(true)}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-lg shadow-lg shadow-emerald-900/20 transition-all font-black text-xs active:scale-95"
            >
              <Download className="w-4 h-4" />
              IMPORTAR TODO
            </button>
            <button 
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-300 px-5 py-2.5 rounded-lg border border-gray-700 shadow-lg transition-all font-black text-xs active:scale-95"
            >
              <Plus className="w-4 h-4" />
              NUEVA ENTIDAD
            </button>
            <button 
              onClick={fetchEndpoints}
              disabled={loading}
              className="flex items-center justify-center w-10 h-10 bg-gray-800 hover:bg-gray-700 text-emerald-500 border border-gray-700 rounded-lg transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 backdrop-blur-sm">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-400 text-sm">Endpoints Configurados</p>
                <p className="text-3xl font-bold mt-1">{endpoints.length}</p>
              </div>
              <div className="bg-cyan-500/10 p-2 rounded-lg text-cyan-500">
                <Settings className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 backdrop-blur-sm">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-400 text-sm">Colecciones MongoDB</p>
                <p className="text-3xl font-bold mt-1 text-purple-400">{endpoints.length}</p>
              </div>
              <div className="bg-purple-500/10 p-2 rounded-lg text-purple-500">
                <HardDrive className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 backdrop-blur-sm">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-400 text-sm">Estado de Conexión</p>
                <p className="text-xl font-bold mt-2 text-green-400 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  YIQI API OK
                </p>
              </div>
              <div className="bg-green-500/10 p-2 rounded-lg text-green-500">
                <RefreshCw className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex flex-col md:flex-row justify-between items-center gap-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Settings className="w-5 h-5 text-cyan-500" />
              Configuración y Ejecución
            </h3>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input 
                type="text"
                placeholder="Filtrar por nombre o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-1.5 text-sm focus:outline-none focus:border-cyan-500/50 transition-colors w-full"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[950px]">
              <div className="grid grid-cols-[1.8fr,0.8fr,0.8fr,1.4fr,1.5fr,1.2fr] bg-gray-900/50 text-gray-400 text-xs uppercase tracking-wider font-medium">
                <div className="px-6 py-4">Entidad / Campos</div>
                <div className="px-6 py-4">Schema ID</div>
                <div className="px-6 py-4">Smartie ID</div>
                <div className="px-6 py-4">Colección Destino</div>
                <div className="px-6 py-4">Último Status</div>
                <div className="px-6 py-4 text-right">Acciones</div>
              </div>

              <div className="divide-y divide-gray-700/50">
                {renderTableBody()}
              </div>
            </div>
          </div>
        </div>

        {/* Info Footer */}
        <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg p-4 flex gap-3 text-sm text-blue-300/80">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-blue-400" />
          <p>
            Desde este panel puedes gestionar los puntos de conexión con el ERP YIQI. 
            Al "Importar", se descargará un Excel, se limpiará la colección local en MongoDB 
            y se recreará con los datos frescos. Los campos se sincronizan automáticamente tras cada éxito.
          </p>
        </div>
      </div>

      {/* ABM Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/50">
              <h3 className="text-xl font-bold text-white">
                {editingId ? 'Editar Endpoint' : 'Nuevo Endpoint'}
              </h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSaveEndpoint}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">ID del Endpoint</label>
                  <input 
                    type="text"
                    required
                    disabled={!!editingId}
                    placeholder="ej: stock_diario"
                    value={formData.id}
                    onChange={(e) => setFormData({...formData, id: e.target.value.toLowerCase().replace(/ /g, '_')})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {!editingId && <p className="text-[10px] text-gray-500 mt-1">Este ID se usa internamente y no puede cambiarse luego.</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Nombre de la Entidad (URL path YIQI)</label>
                  <input 
                    type="text"
                    required
                    placeholder="ej: STOCK_REAL"
                    value={formData.entity_name}
                    onChange={(e) => setFormData({...formData, entity_name: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Smartie Name <span className="text-purple-400">(Nombre de colección MongoDB)</span></label>
                  <input 
                    type="text"
                    placeholder="ej: stock_limpionline_full"
                    value={formData.smartie_name}
                    onChange={(e) => setFormData({...formData, smartie_name: e.target.value})}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Si se deja vacío, se usará el Nombre de Entidad como colección.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Smartie ID</label>
                    <input 
                      type="text"
                      required
                      placeholder="XXXX"
                      value={formData.smartie_id}
                      onChange={(e) => setFormData({...formData, smartie_id: e.target.value})}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Schema ID (Opcional)</label>
                    <input 
                      type="text"
                      placeholder="YYYY"
                      value={formData.schema_id}
                      onChange={(e) => setFormData({...formData, schema_id: e.target.value})}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Descripción</label>
                  <textarea 
                    placeholder="Describe el propósito de este endpoint..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="2"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                  />
                </div>

                <div className="flex items-center gap-3 bg-gray-900/50 p-3 rounded-lg border border-gray-700/50">
                  <div className="flex items-center h-5">
                    <input
                      id="auto_index_rag"
                      type="checkbox"
                      checked={formData.auto_index_rag}
                      onChange={(e) => setFormData({...formData, auto_index_rag: e.target.checked})}
                      className="w-4 h-4 bg-gray-900 border-gray-700 rounded text-cyan-600 focus:ring-cyan-500 focus:ring-offset-gray-900"
                    />
                  </div>
                  <div className="text-sm">
                    <label htmlFor="auto_index_rag" className="font-medium text-white cursor-pointer select-none">
                      Auto-indexar RAG (FAISS)
                    </label>
                    <p className="text-[10px] text-gray-500">
                      Actualiza automáticamente el cerebro de la IA al terminar la importación (OpenAI cost).
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-900/50 flex justify-end gap-3 mt-2">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="bg-cyan-600 hover:bg-cyan-500 text-white px-6 py-2 rounded-lg text-sm font-bold transition-all shadow-lg shadow-cyan-900/20 active:scale-95"
                >
                  {editingId ? 'Guardar Cambios' : 'Crear Endpoint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import All Modal */}
      {isImportAllModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/50">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-cyan-400" />
                Importación Masiva
              </h3>
              {!isImportingAll && (
                <button 
                  onClick={() => setIsImportAllModalOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>
            
            <div className="p-6 space-y-4">
              {/* Info row */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <p className="text-sm text-gray-400">
                  Importará <span className="text-white font-medium">{endpoints.length} endpoints</span> secuencialmente.
                  {endpoints.filter(e => e.auto_index_rag).length > 0 && (
                    <span className="text-emerald-400 ml-2">· {endpoints.filter(e => e.auto_index_rag).length} con RAG AUTO-SYNC</span>
                  )}
                </p>

                {/* Re-index toggle */}
                {endpoints.some(e => e.auto_index_rag) && (
                  <label className="flex items-center gap-2 cursor-pointer shrink-0 bg-gray-900/50 px-3 py-2 rounded-lg border border-emerald-500/20 select-none">
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={resetRagOnImportAll}
                        onChange={e => setResetRagOnImportAll(e.target.checked)}
                        disabled={isImportingAll}
                      />
                      <div className={`w-9 h-5 rounded-full transition-colors ${resetRagOnImportAll ? 'bg-emerald-500' : 'bg-gray-600'}`} />
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${resetRagOnImportAll ? 'translate-x-4' : 'translate-x-0'}`} />
                    </div>
                    <span className="text-xs font-medium text-emerald-300">Re-indexar RAG completo</span>
                  </label>
                )}
              </div>

              <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl">
                 <div className="px-4 py-2 border-b border-gray-800/50 bg-black/20 flex items-center justify-between">
                   <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-2">
                     <Terminal className="w-3.5 h-3.5 text-cyan-500" /> Trazabilidad del proceso
                   </span>
                   <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-500/50" />
                      <div className="w-2 h-2 rounded-full bg-amber-500/50" />
                      <div className="w-2 h-2 rounded-full bg-green-500/50" />
                   </div>
                 </div>
                 <textarea
                   readOnly
                   value={importAllLogs.length > 0 ? importAllLogs.join('\n') : 'Presiona "Iniciar Importación" para comenzar...'}
                   className="w-full h-80 bg-[#0F1117] p-4 text-xs font-mono text-green-500 focus:outline-none resize-none overflow-y-auto custom-scrollbar"
                 />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-900/50 flex justify-end gap-3 mt-2">
              {!isImportingAll && (
                 <button 
                   onClick={() => setIsImportAllModalOpen(false)}
                   className="px-6 py-2.5 text-sm font-black text-gray-400 hover:text-white transition-colors"
                 >
                   Cerrar
                 </button>
              )}
              <button 
                onClick={handleImportAll}
                 disabled={isImportingAll || endpoints.length === 0}
                 className={`flex items-center gap-2 px-8 py-2.5 rounded-lg text-sm font-black transition-all shadow-lg ${
                   isImportingAll 
                     ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                     : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-cyan-900/20 active:scale-95'
                 }`}
               >
                {isImportingAll ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 fill-current" />
                )}
                {isImportingAll ? 'Importando...' : 'Iniciar Importación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
};

export default YiqiImportPage;
